import { gsap } from "gsap";
import { Container, Sprite, Texture } from "pixi.js";

import { EventsManager } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { BackgroundNames, WinType } from "../../../abstracts/types/enums";
import { IWinLevel, ScreenProperties } from "../../../abstracts/types/interfaces";
import { ColorSet, log } from "../../../abstracts/utils/AbstractConsole";
import { generateScaledSprite } from "../../../abstracts/utils/GraphicUtils";
import { getRandomElement, getScreenProperties } from "../../../abstracts/utils/JSUtils";
import { ApocalypseSpineNames } from "../../assetsLoader/ApocalypseAssetsNames";
import {
    ANIMATE_BG_BILLBOARDS,
    HIDE_FS_ELEMENTS,
    RESTORE_BG_BILLBOARDS,
    SHOW_EASTER_EGG_ANIMATION,
    SHOW_FS_BILLBOARD,
    SHOW_GREEN_BILLBOARD,
} from "../../events/ApocalypseEvents";
import { ApocalypseBackgroundNames } from "../../types/ApocalypseEnums";

export class ApocalypseBackgroundView extends AbstractView {
    private normalBackground: Container;
    private freespinBackground: Container;
    private superFreespinBackground: Container;
    private cataclysmBackground: Container;
    private currentBg: Container;

    private fireSpine: AbstractSpine;
    private smokeSpine: AbstractSpine;

    private billboards: AbstractSpine[];

    private frontBillboard: AbstractSpine;
    private hahaBillboard: AbstractSpine;

    private lostSignalSpine: AbstractSpine;
    private lostSignalIdleDelay: number = 10;
    private lostSignalIdleTween: gsap.core.Tween;

    private redScreenSpine: AbstractSpine;
    private redScreenIdleDelay: number = 10;
    private redScreenIdleTween: gsap.core.Tween;

    private greenScreenSpine: AbstractSpine;
    private greenScreenIdleDelay: number = 15;
    private greenScreenIdleTween: gsap.core.Tween;

    private winningBillboardSpine: AbstractSpine | null = null;

    private endBillboard: AbstractSpine;

    private logoLandscape: Sprite;
    private logoPortrait: Sprite;

    constructor() {
        super(`ApocalypseBackgroundView`);

        this.redScreenIdleDelay = 10;
        this.greenScreenIdleDelay = 15;

        EventsManager.on(HIDE_FS_ELEMENTS, () => {
            if (this.currentBg !== this.normalBackground) {
                gsap.to(this.currentBg.getChildByName(`garbagePile`), { alpha: 0, duration: 0.2 });
            }
        });

        let currentWinningBillboardCounter = 0;
        EventsManager.on(ANIMATE_BG_BILLBOARDS, (winLevel: IWinLevel) => {
            this.winningBillboardSpine = this.billboards[currentWinningBillboardCounter];
            if (this.winningBillboardSpine === this.redScreenSpine) {
                this.redScreenIdleTween.kill();
            }

            if (this.winningBillboardSpine === this.lostSignalSpine) {
                this.lostSignalIdleTween.kill();
            }

            this.winningBillboardSpine.reset();
            this.winningBillboardSpine.getSpineTween(`win${getRandomElement([``, `2`])}`, true);

            this.hahaBillboard.reset();
            this.hahaBillboard.getSpineTween(`win${getRandomElement([``, `2`])}`, true);

            currentWinningBillboardCounter++;
            if (currentWinningBillboardCounter > this.billboards.length - 1) {
                currentWinningBillboardCounter = 0;
            }

            let greenSpineAnimationName = `win`;
            if (winLevel.winType !== WinType.TINY && winLevel.winType !== WinType.SMALL) {
                greenSpineAnimationName = `win2`;
            }

            this.greenScreenIdleTween.kill();
            this.greenScreenSpine.reset();
            this.greenScreenSpine.getSpineTween(greenSpineAnimationName, false, null, null, () => {
                this.startGreenScreenIdleTween();
            });
        });

        EventsManager.on(RESTORE_BG_BILLBOARDS, () => {
            this.billboards.forEach((billboard) => {
                billboard.reset();
                if (billboard === this.redScreenSpine) {
                    if (
                        this.winningBillboardSpine !== null &&
                        this.winningBillboardSpine === this.redScreenSpine
                    ) {
                        gsap.delayedCall(this.redScreenIdleDelay, () => {
                            this.startRedScrenIdleTween();
                        });
                    } else {
                        this.startRedScrenIdleTween();
                    }
                } else if (billboard === this.lostSignalSpine && this.lostSignalSpine !== null) {
                    gsap.delayedCall(7, () => {
                        this.startLostSignalBillboardIdleTween();
                    });
                } else {
                    billboard.getSpineTween(`bg`, true);
                }
            });

            this.hahaBillboard.reset();
            this.hahaBillboard.getSpineTween(`bg`, true);

            this.winningBillboardSpine = null;
        });

        EventsManager.on(SHOW_EASTER_EGG_ANIMATION, () => {
            this.hahaBillboard.getSpineTween(`easter_egg`, false, null, null, () => {
                this.hahaBillboard.reset();
                this.hahaBillboard.getSpineTween(`bg`, true);
            });
        });

        EventsManager.on(SHOW_FS_BILLBOARD, () => {
            this.greenScreenIdleTween.kill();
            this.removeChild(this.greenScreenSpine);
            this.greenScreenSpine.reset();

            this.addChild(this.endBillboard);
            this.endBillboard.getSpineTween(`win`, true);
        });

        EventsManager.on(SHOW_GREEN_BILLBOARD, () => {
            this.removeChild(this.endBillboard);
            this.endBillboard.reset();

            this.addChild(this.greenScreenSpine);
            this.startGreenScreenIdleTween();
        });
    }

    protected initGraphics(): void {
        this.normalBackground = this.createNormalBackground();
        this.freespinBackground = this.createFsBackground();
        this.superFreespinBackground = this.createFsBackground();
        this.cataclysmBackground = this.createCataclysmBackground();

        this.logoLandscape = generateScaledSprite(`logo_landscape`);
        this.logoLandscape.scale.set(1);
        this.logoLandscape.anchor.set(0.5);
        this.logoLandscape.position.set(-90, 472);

        this.logoPortrait = generateScaledSprite(`logo_portrait`);
        this.logoPortrait.scale.set(0.7);
        this.logoPortrait.anchor.set(0.7);
        this.logoPortrait.position.set(350, 780);

        this.fireSpine = new AbstractSpine(ApocalypseSpineNames.BG_FIRE_SPINE);
        this.fireSpine.scale.set(1);
        this.fireSpine.position.set(360);

        this.billboards = [];

        this.lostSignalSpine = new AbstractSpine(ApocalypseSpineNames.BG_LOST_SIGNAL);
        this.lostSignalSpine.scale.set(1);
        this.lostSignalSpine.position.set(360);
        this.addChild(this.lostSignalSpine);
        this.billboards.push(this.lostSignalSpine);
        this.lostSignalIdleTween = gsap.to({}, {});

        gsap.delayedCall(7, this.startLostSignalBillboardIdleTween.bind(this));

        this.redScreenSpine = new AbstractSpine(ApocalypseSpineNames.BG_RED_SCREEN);
        this.redScreenSpine.scale.set(1);
        this.redScreenSpine.position.set(840);
        this.billboards.push(this.redScreenSpine);

        this.startRedScrenIdleTween();

        this.smokeSpine = new AbstractSpine(ApocalypseSpineNames.BG_SMOKE);
        this.smokeSpine.scale.set(1.4, 1);
        this.smokeSpine.position.set(360);
        this.addChild(this.smokeSpine);
        this.handleSmokeSpine();

        this.frontBillboard = new AbstractSpine(ApocalypseSpineNames.BG_FRONT_BILLBOARD);
        this.frontBillboard.scale.set(1);
        this.frontBillboard.position.set(360);
        this.frontBillboard.getSpineTween(`bg`, true);
        this.addChild(this.frontBillboard);
        this.billboards.push(this.frontBillboard);

        this.greenScreenSpine = new AbstractSpine(ApocalypseSpineNames.BG_GREEN_SCREEN);
        this.greenScreenSpine.scale.set(1);
        this.greenScreenSpine.position.set(360);
        this.addChild(this.greenScreenSpine);

        this.startGreenScreenIdleTween();
        // this.billboards.push(this.greenScreenSpine);

        this.hahaBillboard = new AbstractSpine(ApocalypseSpineNames.HAHA_BILLBOARD);
        this.hahaBillboard.scale.set(1);
        this.hahaBillboard.position.set(360);
        this.hahaBillboard.getSpineTween(`bg`, true);
        this.addChild(this.hahaBillboard);

        this.endBillboard = new AbstractSpine(ApocalypseSpineNames.END_BILLBOARD);
        this.endBillboard.scale.set(1);
        this.endBillboard.position.set(360);

        // this.billboards.push(this.endBillboard);

        this.checkLogoPosition();
    }

    private startRedScrenIdleTween(): void {
        this.redScreenIdleTween = this.redScreenSpine.getSpineTween(`bg`, false, null, null, () => {
            this.redScreenIdleTween.kill();

            gsap.delayedCall(this.redScreenIdleDelay, () => {
                this.startRedScrenIdleTween();
            });
        });
    }

    private startGreenScreenIdleTween(): void {
        this.greenScreenIdleTween = this.greenScreenSpine.getSpineTween(
            `bg`,
            false,
            null,
            null,
            () => {
                this.greenScreenIdleTween.kill();

                gsap.delayedCall(this.greenScreenIdleDelay, () => {
                    this.startGreenScreenIdleTween();
                });
            }
        );
    }

    private startLostSignalBillboardIdleTween(): void {
        this.lostSignalIdleTween = this.lostSignalSpine.getSpineTween(
            `bg`,
            false,
            null,
            null,
            () => {
                this.lostSignalIdleTween.kill();

                gsap.delayedCall(this.lostSignalIdleDelay, () => {
                    this.startLostSignalBillboardIdleTween();
                });
            }
        );
    }

    private swapBackgroundTo(newBackground: Sprite | Container) {
        if (newBackground === this.currentBg) {
            return;
        }
        newBackground.position.set(-480);

        this.addChildAt(newBackground, 0);
        this.removeChild(this.currentBg);
        this.currentBg = newBackground;

        this.currentBg.addChild(this.redScreenSpine);

        if (newBackground !== this.normalBackground) {
            this.currentBg.addChild(this.currentBg.getChildByName(`garbagePile`));
            this.currentBg.getChildByName(`garbagePile`).alpha = 1;
        }
        // }

        log(`ApocalypseBackgroundView set ` + this.gameMode.toString(), ColorSet.BLACK);
    }

    private createNormalBackground(): Container {
        const backgroundContainer = new Container();
        backgroundContainer.name = BackgroundNames.NORMAL;

        const bgImage = new Sprite(Texture.from(BackgroundNames.NORMAL));
        bgImage.name = `bgImage`;
        backgroundContainer.addChild(bgImage);

        return backgroundContainer;
    }

    private createFsBackground(): Container {
        const backgroundContainer = new Container();
        backgroundContainer.name = BackgroundNames.FREESPIN;

        const bgImage = new Sprite(Texture.from(BackgroundNames.FREESPIN));
        bgImage.name = `bgImage`;
        backgroundContainer.addChild(bgImage);

        const garbagePile = generateScaledSprite(`apocalypse/apocalypse_counter_stand_FS_2`);
        garbagePile.name = `garbagePile`;
        garbagePile.x = 971;
        garbagePile.y = 686;
        backgroundContainer.addChild(garbagePile);

        return backgroundContainer;
    }

    private createCataclysmBackground(): Container {
        const backgroundContainer = new Container();
        backgroundContainer.name = ApocalypseBackgroundNames.CATACLYSM;

        const bgImage = new Sprite(Texture.from(ApocalypseBackgroundNames.CATACLYSM));
        bgImage.name = `bgImage`;
        backgroundContainer.addChild(bgImage);

        const garbagePile = generateScaledSprite(`apocalypse/apocalypse_counter_stand_FS_3`);
        garbagePile.name = `garbagePile`;
        garbagePile.x = 971;
        garbagePile.y = 686;
        backgroundContainer.addChild(garbagePile);

        return backgroundContainer;
    }

    private handleSmokeSpine(): void {
        const tl = gsap.timeline();
        const duration: number = 10;
        this.smokeSpine.alpha = 0;
        tl.add(
            gsap.to(this.smokeSpine, {
                alpha: 1,
                duration: 1,
                onStart: () => {
                    this.addChild(this.smokeSpine);
                    this.smokeSpine.getSpineTween(`bg`, true);
                },
            }),
            0
        );

        tl.add(
            gsap.to(this.smokeSpine, {
                alpha: 0,
                duration: 1,
                onComplete: () => {
                    this.smokeSpine.reset();
                    this.removeChild(this.smokeSpine);
                },
            }),
            duration / 2
        );

        tl.call(() => this.handleSmokeSpine(), undefined, duration * 2);
    }

    private checkLogoPosition(): void {
        const screenProps: ScreenProperties = getScreenProperties();

        if (screenProps.isLandscape) {
            this.removeChild(this.logoPortrait);
            this.addChild(this.logoLandscape);
        } else {
            this.removeChild(this.logoLandscape);

            this.logoPortrait.position.set(350, 708 + screenProps.bounds.height * 0.08);
            this.addChild(this.logoPortrait);
        }
    }

    private checkGarbagePilePosition(): void {
        const screenProps: ScreenProperties = getScreenProperties();

        if (screenProps.isLandscape) {
            this.removeChild(this.logoPortrait);
            this.addChild(this.logoLandscape);

            if (this.gameMode !== AbstractGameMode.NORMAL) {
                const garbagePile = this.currentBg.getChildByName(`garbagePile`);
                garbagePile.x = 971;

                if (screenProps.isMobile) {
                    const resolution = screenProps.bounds.height / screenProps.bounds.width;

                    if (resolution > 0.73 && resolution < 0.76) {
                        garbagePile.x = 946;
                        // this.fsMultiplierContainer.y = -220;
                    }
                }
            }
        } else {
            this.removeChild(this.logoLandscape);
            this.addChild(this.logoPortrait);
        }
    }

    protected startFire(): void {
        this.addChild(this.fireSpine);

        this.fireSpine.getSpineTween(`fire`, true);
    }

    protected stopFire(): void {
        this.removeChild(this.fireSpine);

        this.fireSpine.reset();
    }

    public onResize(): void {
        this.checkLogoPosition();
        this.checkGarbagePilePosition();
    }

    protected onGameModeUpdate() {
        super.onGameModeUpdate();

        switch (this.gameMode) {
            case AbstractGameMode.FREESPIN:
                this.swapBackgroundTo(this.freespinBackground);
                this.stopFire();
                break;

            case AbstractGameMode.SUPER_FREESPIN:
                this.swapBackgroundTo(this.superFreespinBackground);
                this.startFire();
                break;

            case AbstractGameMode.FREESPIN_BETTER:
                this.swapBackgroundTo(this.cataclysmBackground);
                this.startFire();

                break;

            default:
                this.swapBackgroundTo(this.normalBackground);
                this.stopFire();

                break;
        }
    }

    public showNextBackground(newGameMode: AbstractGameMode) {
        this.gameMode = newGameMode;

        this.onGameModeUpdate();
    }

    public onOrientationChanged() {
        //handle
    }
}
